/**
 * Profile Activity root (the /profile route). Connects to Discord (or the Mock SDK) exactly like the
 * review app (useActivity → Bearer token), loads the member's profile, and renders the four
 * bot-sectioned, skinned windows: Brockett, Sophie, Pennysaver, Steel Curtain.
 *
 * Tier (is_supporter) comes from GET /api/profile. Free members see the Supporter-only blocks as
 * stamped EXAMPLES; the server refuses Supporter writes regardless, so the client tier check is just UX.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Preferences, Profile, ProfileResponse, ProfileUpdate } from '@pghbites/shared';
import { useActivity, displayName } from '../state/useActivity.js';
import {
  addNote, forgetMe, getProfile, putProfile, putVoice, removeNote, removeObservation,
} from './profileApi.js';
import { BrockettWindow } from './BrockettWindow.js';
import { SophieWindow } from './SophieWindow.js';
import { PennysaverWindow } from './PennysaverWindow.js';
import { SteelCurtainWindow } from './SteelCurtainWindow.js';
import './profile.css';

type Save = 'idle' | 'saving' | 'saved' | 'error';

function ProfileBody({ name }: { name: string }) {
  const [data, setData] = useState<ProfileResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [save, setSave] = useState<Save>('idle');
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProfile()
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Could not load your profile.'); });
    return () => { cancelled = true; };
  }, []);

  const flashSaved = useCallback(() => {
    setSave('saved');
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSave('idle'), 1500);
  }, []);
  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  const patchLocal = useCallback((patch: { profile?: Partial<Profile>; preferences?: Partial<Preferences> }) => {
    setData((d) => (d ? {
      ...d,
      profile: { ...d.profile, ...patch.profile },
      preferences: { ...d.preferences, ...patch.preferences },
    } : d));
  }, []);

  // ── Free-field saves (optimistic, then PUT /api/profile) ──────────────────────
  const saveFree = useCallback(async (patch: Parameters<typeof putProfile>[0], local: { profile?: Partial<Profile>; preferences?: Partial<Preferences> }) => {
    patchLocal(local);
    setSave('saving');
    try {
      const fresh = await putProfile(patch);
      setData(fresh);
      flashSaved();
    } catch {
      setSave('error');
    }
  }, [patchLocal, flashSaved]);

  // ── Supporter saves (PUT /api/profile/voice) ──────────────────────────────────
  const saveVoice = useCallback(async (patch: Partial<Preferences>) => {
    patchLocal({ preferences: patch });
    setSave('saving');
    const ok = await putVoice(patch as Parameters<typeof putVoice>[0]);
    if (ok) flashSaved(); else setSave('error');
  }, [patchLocal, flashSaved]);

  if (loadError) {
    return (
      <main className="profile-app"><div className="content"><div className="center-screen" role="alert">
        <p>We couldn&rsquo;t load your profile.</p><p className="muted">{loadError}</p>
      </div></div></main>
    );
  }
  if (!data) {
    return (
      <main className="profile-app"><div className="content"><div className="center-screen" aria-busy="true">
        <p className="muted">Loading your profile…</p>
      </div></div></main>
    );
  }

  const { profile, preferences, is_supporter: isSupporter } = data;

  const toggleArray = (field: 'fav_cuisines' | 'dietary_avoidances' | 'sports_teams', value: string) => {
    const cur = profile[field];
    const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
    void saveFree({ [field]: next } as ProfileUpdate, { profile: { [field]: next } as Partial<Profile> });
  };

  return (
    <main className="profile-app">
      <div className="glow" />
      <div className="content">
        <div className="hero">
          <div className="ring"><div className="face">🍴</div></div>
          <h1>Your <b>PGH Bites</b> Profile</h1>
          <div className="who">
            {name}
            <span className={`badge${isSupporter ? '' : ' free'}`}>{isSupporter ? '★ Supporter' : 'Free member'}</span>
          </div>
        </div>

        <BrockettWindow
          profile={profile}
          preferences={preferences}
          isSupporter={isSupporter}
          onToggleArray={toggleArray}
          onSetPrice={(v) => saveFree({ price_range: v }, { profile: { price_range: v } })}
          onSetDial={(key, v) => saveVoice({ [key]: v } as Partial<Preferences>)}
          onAddNote={async (text) => {
            setSave('saving');
            const notes = await addNote(text);
            if (notes) { patchLocal({ preferences: { notebook_notes: notes } }); flashSaved(); } else setSave('error');
          }}
          onRemoveNote={async (id) => {
            setSave('saving');
            const notes = await removeNote(id);
            if (notes) { patchLocal({ preferences: { notebook_notes: notes } }); flashSaved(); } else setSave('error');
          }}
          onRemoveObservation={async (id) => {
            setSave('saving');
            const ok = await removeObservation(id);
            if (ok) {
              patchLocal({ preferences: { notebook_observations: preferences.notebook_observations.filter((o) => o.id !== id) } });
              flashSaved();
            } else setSave('error');
          }}
        />

        <SophieWindow
          profile={profile}
          onSetField={(field, v) => saveFree({ [field]: v } as ProfileUpdate, { profile: { [field]: v } as Partial<Profile> })}
        />

        <PennysaverWindow
          preferences={preferences}
          isSupporter={isSupporter}
          onSetReviewVoice={(v) => saveFree({ review_voice: v }, { preferences: { review_voice: v } })}
          onSetCustomVoice={(v) => saveVoice({ custom_review_voice: v })}
        />

        <SteelCurtainWindow
          profile={profile}
          preferences={preferences}
          onToggle={(field, value) => {
            if (field === 'tracking_enabled') saveFree({ tracking_enabled: value }, { profile: { tracking_enabled: value } });
            else saveFree({ [field]: value } as ProfileUpdate, { preferences: { [field]: value } as Partial<Preferences> });
          }}
          onForgetMe={async () => {
            const sure = typeof window !== 'undefined'
              ? window.confirm('Wipe everything PGH Bites remembers about you? This cannot be undone. (Your posted reviews + moderation history stay.)')
              : true;
            if (!sure) return;
            setSave('saving');
            const ok = await forgetMe();
            if (ok) {
              setData({
                is_supporter: false,
                profile: { preferred_name: '', zip_code: '', price_range: '', sports_teams: [], dietary_avoidances: [], fav_cuisines: [], tracking_enabled: true },
                preferences: {
                  liked_cuisines: [], disliked_cuisines: [],
                  voice_humor: 3, voice_local_pride: 3, voice_formality: 3, voice_detail: 3, voice_adventure: 3,
                  review_voice: '', custom_review_voice: '', notebook_observations: [], notebook_notes: [],
                  profile_private: false, allow_member_dms: false, notify_my_reviews: true,
                },
              });
              flashSaved();
            } else setSave('error');
          }}
        />

        <div className="savestate" role="status" aria-live="polite">
          {save === 'saving' && 'Saving…'}
          {save === 'saved' && 'Saved 🖤💛'}
          {save === 'error' && 'Couldn’t save — try again.'}
        </div>
        <div className="footer">PGH Bites 🖤💛</div>
      </div>
    </main>
  );
}

export function ProfileApp() {
  const state = useActivity();

  if (state.status === 'connecting') {
    return (
      <main className="profile-app"><div className="content"><div className="center-screen" aria-busy="true">
        <p className="muted">Connecting to Discord…</p>
      </div></div></main>
    );
  }
  if (state.status === 'error') {
    return (
      <main className="profile-app"><div className="content"><div className="center-screen" role="alert">
        <p>We couldn&rsquo;t connect.</p><p className="muted">{state.message}</p>
      </div></div></main>
    );
  }
  return <ProfileBody name={displayName(state.ctx)} />;
}
